/**
 * `meetings` procedure group.
 *
 * Wave 1 shipped exactly one procedure — `meetings.upcoming`, the macOS
 * recorder's arming feed. Wave-2 track F2 adds the other ten: the lifecycle the
 * Mac drives (`start` / `finish` / `discard` / `transcript.submit`) and the read
 * surface the web meeting page needs (`transcript.get`, `summary.get`,
 * `summary.regenerate`, `highlights.list`, `speakers.list`, `speakers.assign`).
 *
 * ## Three shapes here are load-bearing rather than stylistic
 *
 * **`start` never throws for a blocked meeting.** A do-not-record attendee is a
 * legitimate, expected answer to "may I record this", not an error — and the Mac
 * has to render the reason in the menu bar (§4.7: the app "refuses to arm and
 * SAYS WHY"). A 403 would arrive as a generic failure through the retry layer
 * and be indistinguishable from a network problem. So the refusal is a FIELD.
 *
 * **`transcript.submit` is chunked and idempotent.** §4.5 has the upload queue
 * surviving quit, crash and sleep over whatever network the user has, so a
 * 90-minute meeting's few thousand segments arrive as bounded slices keyed by
 * absolute index. The cap is 500 per call.
 *
 * **`summary.regenerate` enqueues; it does not rebuild inline.** A regenerate is
 * a TOP-tier call over a whole transcript. A procedure that did that inline
 * would let a double-tap, a retry or a prefetch pay for it twice — the same
 * reasoning as `hermes.digest.regenerate`.
 */

import { z } from 'zod';
import { isoDateTimeSchema, nonBlankString, uuidSchema } from './common.ts';
import { meetingProviderSchema, recordingConsentModeSchema, syncProviderSchema } from './enums.ts';

// --- shared vocabulary -------------------------------------------------------

/** `meetings.consent_mode`, mirroring `meetings_consent_ck`. */
export const MEETING_CONSENT_MODES = ['disclosed', 'not_disclosed', 'declined'] as const;
export type MeetingConsentMode = (typeof MEETING_CONSENT_MODES)[number];
export const meetingConsentModeSchema = z.enum(MEETING_CONSENT_MODES);

/** `meetings.status`, mirroring `meetings_status_ck`. */
export const MEETING_STATUSES = [
  'recording',
  'uploaded',
  'transcribing',
  'transcribed',
  'summarizing',
  'complete',
  'failed',
  'discarded',
] as const;
export type MeetingStatusValue = (typeof MEETING_STATUSES)[number];
export const meetingStatusSchema = z.enum(MEETING_STATUSES);

/** Mirrors the Swift `StopReason`; known only on the device. */
export const MEETING_STOP_REASONS = [
  'manual',
  'sustainedSilenceAndProcessExit',
  'calendarEndPlusGrace',
  'captureFailed',
  'killSwitch',
] as const;
export const meetingStopReasonSchema = z.enum(MEETING_STOP_REASONS);

/**
 * The computed consent verdict — `@crm/domain`'s `ConsentPosture`, on the wire.
 *
 * Duplicated here rather than imported: `@crm/contracts` deliberately takes no
 * dependency on `@crm/domain` (it must stay importable by a service worker with
 * zero transitive weight), exactly as `CONTACT_TYPES` and `INTERACTION_KINDS`
 * are duplicated. `contract.test.ts` asserts the two tuples stay identical.
 */
export const CONSENT_POSTURES = ['allowed', 'disclosure_required', 'blocked'] as const;
export type ConsentPostureValue = (typeof CONSENT_POSTURES)[number];
export const consentPostureSchema = z.enum(CONSENT_POSTURES);

export const CONSENT_REASONS = [
  'org_never_record',
  'contact_do_not_record',
  'org_disclose_always',
  'all_party_jurisdiction',
  'one_party_jurisdiction',
] as const;
export const consentReasonSchema = z.enum(CONSENT_REASONS);

export const TRANSCRIPT_TRACKS = ['mic', 'app'] as const;
export type TranscriptTrack = (typeof TRANSCRIPT_TRACKS)[number];
export const transcriptTrackSchema = z.enum(TRANSCRIPT_TRACKS);

export const SPEAKER_METHODS = [
  'track_construction',
  'voiceprint',
  'calendar_llm',
  'manual',
  'unresolved',
] as const;
export const speakerMethodSchema = z.enum(SPEAKER_METHODS);

export const HIGHLIGHT_KINDS = [
  'key_point',
  'decision',
  'action_item',
  'question',
  'risk',
  'quote',
] as const;
export type HighlightKind = (typeof HIGHLIGHT_KINDS)[number];
export const highlightKindSchema = z.enum(HIGHLIGHT_KINDS);

// --- meetings.upcoming -------------------------------------------------------

/** Design §4.3 spells this `crmContactId?`; the field is `contactId`, nullable. */
export const upcomingAttendeeSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
  /** `null` when the address matches no CRM contact — an explicit "stranger". */
  contactId: uuidSchema.nullable(),
  responseStatus: z.string().nullable(),
  isOrganizer: z.boolean(),
  isSelf: z.boolean(),
  isOptional: z.boolean(),
  /** True when THIS attendee is the reason the meeting cannot be recorded. */
  doNotRecord: z.boolean(),
});

export const upcomingMeetingSchema = z.object({
  /** The `calendar_events` row id. Stable across sync rounds. */
  eventId: uuidSchema,
  connectedAccountId: uuidSchema,
  accountProvider: syncProviderSchema,
  title: z.string().nullable(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  /**
   * The conference vendor, `null` when a join URL was found but the catalog did
   * not recognise it. Never guessed.
   */
  provider: meetingProviderSchema.nullable(),
  joinUrl: z.string().nullable(),
  location: z.string().nullable(),
  organizerEmail: z.string().nullable(),
  selfResponse: z.string().nullable(),
  attendees: z.array(upcomingAttendeeSchema),
  /**
   * "if any attendee resolves to a CRM contact flagged do-not-record, the app
   * refuses to arm and says why" (clients design §4.3).
   */
  doNotRecord: z.boolean(),
  doNotRecordReason: z.string().nullable(),
  /**
   * PER MEETING, computed (Wave-2 track F2 — this replaced the org-default stub).
   * `blocked` means the recorder must not arm; `disclosure_required` means it may
   * arm only with the pre-roll banner and the disclosure snippet.
   */
  consentPosture: consentPostureSchema,
  consentReason: consentReasonSchema,
  /** One sentence the menu bar shows verbatim. */
  consentReasonText: z.string(),
});

export const meetingsUpcomingInputSchema = z.object({
  /** Defaults to server `now`; supplied so an offline queue can backfill. */
  at: isoDateTimeSchema.optional(),
  /** The recorder asks for 4h; the cap keeps one poll from paging a month. */
  windowMinutes: z.int().min(5).max(1_440).default(240),
  /**
   * Grace for a meeting already in progress. The recorder starts late (a laptop
   * wakes at T+3), and a feed that only shows the future would never arm it.
   */
  graceMinutes: z.int().min(0).max(120).default(15),
});

export const meetingsUpcomingOutputSchema = z.object({
  items: z.array(upcomingMeetingSchema),
  asOf: isoDateTimeSchema,
  /** The org's recording posture, from `org_settings`. */
  consentPolicy: recordingConsentModeSchema,
  /**
   * §4.7's "disclosure snippet Hermes can drop into the calendar invite
   * description or meeting chat". A server-owned CONSTANT, delivered with the
   * feed so the Mac renders exactly the text this deployment agreed to and never
   * a paraphrase of it.
   */
  disclosureSnippet: z.string(),
  disclosureSnippetShort: z.string(),
});

// --- the meeting record ------------------------------------------------------

export const meetingSchema = z.object({
  id: uuidSchema,
  /** The device's own recording UUID; the idempotency key `start` collides on. */
  clientMeetingId: z.string().nullable(),
  calendarEventId: uuidSchema.nullable(),
  title: z.string().nullable(),
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  durationSeconds: z.int().nullable(),
  provider: meetingProviderSchema.nullable(),
  joinUrl: z.string().nullable(),
  status: meetingStatusSchema,
  consentMode: meetingConsentModeSchema,
  stopReason: meetingStopReasonSchema.nullable(),
  /** Non-null when the pipeline refused or failed; rendered on the meeting page. */
  failureReason: z.string().nullable(),
  processedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export type Meeting = z.output<typeof meetingSchema>;

// --- meetings.start ----------------------------------------------------------

export const meetingsStartInputSchema = z.object({
  /**
   * The Mac's LOCAL recording UUID. §4.5 keys idempotency on it, so it must be
   * minted before capture and replayed verbatim on every retry.
   */
  clientMeetingId: nonBlankString(128, 'clientMeetingId'),
  calendarEventId: uuidSchema.nullish(),
  deviceId: uuidSchema.nullish(),
  title: z.string().max(500).nullish(),
  startedAt: isoDateTimeSchema,
  provider: meetingProviderSchema.nullish(),
  joinUrl: z.string().max(2_048).nullish(),
  /** What the §4.7 pre-roll banner actually did. */
  consentMode: meetingConsentModeSchema,
  consentRecordedAt: isoDateTimeSchema.nullish(),
});

/**
 * FLAT rather than a discriminated union.
 *
 * A refusal is an expected answer, and the Swift client decodes this into one
 * struct: `blocked` plus its reason, or a meeting. A union would generate two
 * Swift types and an enum for a distinction the caller resolves with one `if`.
 */
export const meetingsStartOutputSchema = z.object({
  /** True when a do-not-record attendee or the org policy refused the recording. */
  blocked: z.boolean(),
  consentPosture: consentPostureSchema,
  consentReason: consentReasonSchema,
  /** The sentence the menu bar shows. Never empty. */
  message: z.string(),
  blockedContactIds: z.array(uuidSchema),
  /** Null exactly when `blocked` — nothing is written for a refused meeting. */
  meeting: meetingSchema.nullable(),
  /** True when an existing meeting was returned: a replayed `start`. */
  replayed: z.boolean(),
});

// --- meetings.finish ---------------------------------------------------------

export const recordingTrackSchema = z.object({
  track: transcriptTrackSchema,
  /**
   * The §4.2 validity assertion, measured on the device over the whole file.
   * The server REFUSES a recording whose RMS is not strictly positive: a dozen
   * distinct Core Audio misconfigurations all present as perfectly-formed
   * callbacks full of zeros, and measuring the signal is the only defence.
   */
  rmsMean: z.number(),
  durationSeconds: z.number().nullish(),
  bytes: z.number().int().nullish(),
  sampleRateHz: z.int().min(8_000).max(192_000).default(16_000),
  channels: z.int().min(1).max(2).default(1),
  sha256: z.string().max(128).nullish(),
  /** Audio stays local by default (§4.5); this is a path on the user's Mac. */
  localPath: z.string().max(1_024).nullish(),
});

export const meetingsFinishInputSchema = z.object({
  meetingId: uuidSchema,
  endedAt: isoDateTimeSchema,
  durationSeconds: z.int().min(0).nullish(),
  stopReason: meetingStopReasonSchema.nullish(),
  tracks: z.array(recordingTrackSchema).min(1).max(4),
});

export const meetingsFinishOutputSchema = z.object({
  meeting: meetingSchema,
  /** False when the silent-tap guard refused the recording. */
  accepted: z.boolean(),
  /** Tracks whose RMS was not strictly positive. Empty when accepted. */
  silentTracks: z.array(transcriptTrackSchema),
  message: z.string(),
});

export const meetingsDiscardInputSchema = z.object({
  meetingId: uuidSchema,
  reason: z.string().max(500).nullish(),
});

// --- meetings.transcript.submit ---------------------------------------------

/** §4.5's cap. Above this a single request stops fitting in one body. */
export const MAX_TRANSCRIPT_SEGMENTS_PER_CALL = 500;

export const transcriptSegmentInputSchema = z.object({
  /** ABSOLUTE index in the merged transcript, not the index within this chunk. */
  idx: z.int().min(0),
  track: transcriptTrackSchema,
  /** Diarization label. Always null on `mic` — that track is the user. */
  speakerLabel: z.string().max(64).nullish(),
  startMs: z.int().min(0),
  endMs: z.int().min(0),
  text: z.string().max(10_000),
  confidence: z.number().min(0).max(1).nullish(),
});

/** `contact_voice_prints.embedding` / `meeting_speakers.embedding` are `vector(256)`. */
export const VOICEPRINT_DIMENSIONS = 256;

/**
 * One diarized label's voice embedding — the FOLLOWUPS entry "voice embeddings
 * are not on the wire yet", closed from the server side.
 *
 * The server-side matcher has existed since track F2: `voicePrintsForContacts`,
 * `cosineSimilarity` and the 0.75 threshold are all built and tested. What was
 * missing was the input — `meeting.process` had an `embeddingForLabel` that
 * returned `null`, so every meeting fell through to the closed-set LLM
 * attribution even for people the CRM already had a voiceprint for.
 *
 * ## Why it is OPTIONAL, and why that is not a hedge
 *
 * §4.5 keeps audio local by default, and an embedding is derived from audio. A
 * recorder build that has not shipped the FluidAudio extractor yet, a transcript
 * from AssemblyAI, or a user who declined diarization all submit transcripts
 * with no embeddings — and every one of those must keep working exactly as it
 * does today. Absent means "fall through to the LLM pass", which is the
 * behaviour that exists.
 *
 * ## Why it rides on `transcript.submit` rather than its own procedure
 *
 * The label is only meaningful next to the segments carrying it. A separate
 * upload would be a second request that can fail on its own, leaving a
 * transcript whose labels reference embeddings that never arrived — and the Mac's
 * upload queue would then need two things to retry in order instead of one.
 *
 * It is sent on the chunk that HAS the labels (any chunk; last is natural), not
 * repeated on every chunk: `meeting_speakers` is upserted per label, so a repeat
 * is idempotent but wasteful — 256 floats per speaker per request.
 */
export const speakerEmbeddingInputSchema = z.object({
  /** Must match a `speakerLabel` on this transcript's `app`-track segments. */
  speakerLabel: nonBlankString(64, 'speakerLabel'),
  /**
   * The diarizer's centroid for this label. Exactly 256 dimensions: the column
   * is `vector(256)` and a shorter array is not a smaller vector, it is a
   * different one that cosine would happily score against a stored print.
   */
  embedding: z.array(z.number()).length(VOICEPRINT_DIMENSIONS),
});

export const meetingsTranscriptSubmitInputSchema = z.object({
  meetingId: uuidSchema,
  /** `parakeet_tdt_v3 | whisperkit_lv3turbo | assemblyai_u35`. */
  engine: nonBlankString(64, 'engine'),
  engineVersion: z.string().max(64).nullish(),
  language: z.string().max(16).default('en'),
  /** `fluidaudio_pyannote_c1` when the `_app` track was diarized. */
  diarizationEngine: z.string().max(64).nullish(),
  /** Total segments in the WHOLE transcript, so completeness is checkable. */
  totalSegments: z.int().min(0),
  segments: z.array(transcriptSegmentInputSchema).max(MAX_TRANSCRIPT_SEGMENTS_PER_CALL),
  /**
   * Per-diarized-speaker voice embeddings. OPTIONAL — see
   * `speakerEmbeddingInputSchema` for why, and for why they ride here.
   *
   * Capped at 32: a meeting with more than 32 distinct diarized voices is a
   * conference recording, not a conversation, and 32 x 256 floats is already a
   * ~180 KB body next to a 500-segment chunk.
   */
  speakerEmbeddings: z.array(speakerEmbeddingInputSchema).max(32).optional(),
  /** Set on the last chunk. Only believed once every promised segment is here. */
  isFinal: z.boolean().default(false),
});

export const meetingsTranscriptSubmitOutputSchema = z.object({
  transcriptId: uuidSchema,
  storedSegments: z.int().min(0),
  totalSegments: z.int().min(0),
  isFinal: z.boolean(),
  /** True when this call completed the transcript and queued `meeting.process`. */
  processingQueued: z.boolean(),
  /**
   * Voice embeddings recorded on `meeting_speakers`. Echoed so the Mac can tell
   * "the server does not want them" from "the server took them" without parsing
   * a version — an older deployment answers 0 and the client stops sending.
   */
  speakerEmbeddingsStored: z.int().min(0),
});

// --- meetings.transcript.get -------------------------------------------------

export const transcriptSegmentSchema = z.object({
  id: uuidSchema,
  idx: z.int().min(0),
  track: transcriptTrackSchema,
  speakerLabel: z.string().nullable(),
  /** The resolved speaker row, once someone or something named this voice. */
  meetingSpeakerId: uuidSchema.nullable(),
  startMs: z.int().min(0),
  endMs: z.int().min(0),
  text: z.string(),
  confidence: z.number().nullable(),
});

export const transcriptSchema = z.object({
  id: uuidSchema,
  engine: z.string(),
  engineVersion: z.string().nullable(),
  language: z.string(),
  diarizationEngine: z.string().nullable(),
  wordCount: z.int().nullable(),
  segmentCount: z.int().nullable(),
  isFinal: z.boolean(),
  createdAt: isoDateTimeSchema,
});

export const meetingsTranscriptGetInputSchema = z.object({
  meetingId: uuidSchema,
  /** Segments page by absolute INDEX; the sequence is dense and immutable. */
  fromIdx: z.int().min(0).default(0),
  limit: z.int().min(1).max(1_000).default(200),
});

export const meetingsTranscriptGetOutputSchema = z.object({
  meeting: meetingSchema,
  /** Null while a recording is still uploading, or when it was refused. */
  transcript: transcriptSchema.nullable(),
  segments: z.array(transcriptSegmentSchema),
  /** Absolute index of the next page, or null at the end. */
  nextIdx: z.int().min(0).nullable(),
});

// --- meetings.summary --------------------------------------------------------

export const meetingSummarySchema = z.object({
  id: uuidSchema,
  kind: z.enum(['detailed', 'brief']),
  summaryMd: z.string(),
  /** Pinned model id. Model output is never presented as user-authored fact. */
  modelId: z.string(),
  tier: z.string(),
  createdAt: isoDateTimeSchema,
});

export const meetingsSummaryGetInputSchema = z.object({
  meetingId: uuidSchema,
});

export const meetingsSummaryGetOutputSchema = z.object({
  meeting: meetingSchema,
  summary: meetingSummarySchema.nullable(),
});

export const meetingsSummaryRegenerateInputSchema = z.object({
  meetingId: uuidSchema,
});

export const meetingsSummaryRegenerateOutputSchema = z.object({
  ok: z.literal(true),
  meetingId: uuidSchema,
  /** False when a run for this meeting was already in flight. */
  queued: z.boolean(),
  reason: z.string().nullable(),
});

// --- meetings.highlights -----------------------------------------------------

export const meetingHighlightSchema = z.object({
  id: uuidSchema,
  kind: z.string(),
  text: z.string(),
  startMs: z.int().nullable(),
  endMs: z.int().nullable(),
  /** Citation back into the transcript, so a highlight can be jumped to. */
  segmentIdx: z.int().nullable(),
  attributedContactId: uuidSchema.nullable(),
  ownerContactId: uuidSchema.nullable(),
  confidence: z.number().nullable(),
  rank: z.int().nullable(),
});

export const meetingsHighlightsListInputSchema = z.object({
  meetingId: uuidSchema,
  kind: highlightKindSchema.optional(),
});

export const meetingsHighlightsListOutputSchema = z.object({
  items: z.array(meetingHighlightSchema),
});

// --- meetings.speakers -------------------------------------------------------

export const meetingSpeakerSchema = z.object({
  id: uuidSchema,
  track: transcriptTrackSchema,
  speakerLabel: z.string(),
  /** The linked contact. Null means nobody has named this voice yet. */
  resolvedContactId: uuidSchema.nullable(),
  /** The name the pipeline GUESSED. Distinct from a link on purpose. */
  displayName: z.string().nullable(),
  method: speakerMethodSchema,
  confidence: z.number().nullable(),
  confirmedByUser: z.boolean(),
  /** The closed set the attribution pass was constrained to. */
  candidates: z.array(z.string()),
  /** True when a voiceprint exists for the linked contact. */
  hasVoicePrint: z.boolean(),
  segmentCount: z.int().min(0),
});

/** A person the assignment UI may choose: the meeting's resolved attendees. */
export const speakerCandidateSchema = z.object({
  contactId: uuidSchema,
  name: z.string(),
  email: z.string().nullable(),
});

export const meetingsSpeakersListInputSchema = z.object({
  meetingId: uuidSchema,
});

export const meetingsSpeakersListOutputSchema = z.object({
  items: z.array(meetingSpeakerSchema),
  /**
   * The candidate set, sent WITH the list: the assignment UI must offer exactly
   * the people the attribution pass could have chosen, and a second round trip
   * to build the dropdown is a round trip that can fail on its own.
   */
  candidates: z.array(speakerCandidateSchema),
});

export const meetingsSpeakersAssignInputSchema = z.object({
  meetingId: uuidSchema,
  speakerId: uuidSchema,
  /** Null clears the assignment back to "nobody has named this voice". */
  contactId: uuidSchema.nullable(),
  displayName: z.string().max(200).nullish(),
  /**
   * §3.1 step 2c: persist the centroid "on user confirmation", so every
   * subsequent meeting identifies them automatically. Defaults ON, because that
   * flywheel is the point; a user fixing a bad match turns it off so the bad
   * audio is not folded into the contact's print.
   */
  learnVoicePrint: z.boolean().default(true),
});

export const meetingsSpeakersAssignOutputSchema = z.object({
  speaker: meetingSpeakerSchema,
  segmentsRelabelled: z.int().min(0),
  voicePrintUpdated: z.boolean(),
});

// --- inferred types ----------------------------------------------------------

export type UpcomingMeeting = z.output<typeof upcomingMeetingSchema>;
export type MeetingsUpcomingOutput = z.output<typeof meetingsUpcomingOutputSchema>;
export type MeetingsStartOutput = z.output<typeof meetingsStartOutputSchema>;
export type MeetingsFinishOutput = z.output<typeof meetingsFinishOutputSchema>;
export type TranscriptSegment = z.output<typeof transcriptSegmentSchema>;
export type Transcript = z.output<typeof transcriptSchema>;
export type MeetingSummary = z.output<typeof meetingSummarySchema>;
export type MeetingHighlight = z.output<typeof meetingHighlightSchema>;
export type MeetingSpeaker = z.output<typeof meetingSpeakerSchema>;
export type SpeakerCandidate = z.output<typeof speakerCandidateSchema>;
export type MeetingsTranscriptGetOutput = z.output<typeof meetingsTranscriptGetOutputSchema>;
export type MeetingsSpeakersListOutput = z.output<typeof meetingsSpeakersListOutputSchema>;
